import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Clock, CheckCircle, AlertCircle, XCircle, BarChart3,ClipboardList, Folder, Ship, Shield, Zap, Wrench, ChevronDown, ChevronRight, Bell, Search, Filter, Menu, Target, Anchor, Navigation, ChevronLeft, ChevronUp, Maximize2, Minimize2, CheckSquare } from 'lucide-react';
import EmployeeShiftCalender from '../projectmappingdashboard/EmployeeShiftCalender';
import WorkOrderCalender from '../projectmappingdashboard/WorkOrderCalender';
import DepartmentWiseMapping from '../projectmappingdashboard/DepartmentWiseMapping'; // Import DepartmentWiseMapping
import RequisitionScheduler from '../calender/Requisition/RequisitionScheduler'; // Import RequisitionScheduler
import { getRequest, postRequest } from '../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Corrected import path, removed .min.css


const AssistanceWarshipSuperintendentDashboard = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all'); // Changed to 'all' to store ID
  const [selectedProject, setSelectedProject] = useState(1);
  const [selectedShip, setSelectedShip] = useState(12);
  const [attendance, setAttendance] = useState({ checkIn: '', checkOut: '' });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isShipDropdownOpen, setIsShipDropdownOpen] = useState(false);
  const [isEmployeeCalendarFullscreen, setIsEmployeeCalendarFullscreen] = useState(false);
  const [isWorkOrderCalendarFullscreen, setIsWorkOrderCalendarFullscreen] = useState(false);
  const [isRequisitionCalendarFullscreen, setIsRequisitionCalendarFullscreen] = useState(false); // New state for RequisitionScheduler fullscreen
  const [isCheckInButtonDisabled, setIsCheckInButtonDisabled] = useState(false); // New state for check-in button disabling

  // New state for API data
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [departments, setDepartments] = useState([]); // New state for departments
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShips, setLoadingShips] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false); // New loading state for departments
  const [loadingTaskStats, setLoadingTaskStats] = useState(false); // New loading state for task stats

  // New state for the custom pop-up (Project Wise)
  const [showNewPopup, setShowNewPopup] = useState(false);

  // New state for Department Wise Mapping pop-up
  const [showDepartmentMapping, setShowDepartmentMapping] = useState(false);

  // Ref for detecting clicks outside dropdowns
  const dropdownRef = useRef(null);

  // State for API-fetched task statistics
  const [taskStats, setTaskStats] = useState({
    total_tasks: 0,
    in_progress_tasks: 0,
    assigned_tasks: 0,
    not_assigned_tasks: 0,
    delayed_tasks: 0
  });

  // Function to get user ID from localStorage
  const getUserId = () => {
    try {
      const storedAuthDataString = localStorage.getItem('authData');
      if (storedAuthDataString) {
        const authData = JSON.parse(storedAuthDataString);
        return authData.user_id;
      }
    } catch (error) {
      console.error("Error parsing authData from localStorage:", error);
    }
    return null;
  };

  // Helper function to format ISO date string to DD/MM/YYYY HH:MM:SS AM/PM IST
  const formatISTDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true, // For AM/PM
        timeZone: 'Asia/Kolkata', // IST timezone
      };
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    } catch (e) {
      console.error("Error formatting date:", e);
      return isoString; // Return original if formatting fails
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // New useEffect to fetch attendance data and disable check-in button
  useEffect(() => {
    const fetchAttendanceStatus = async () => {
      const userId = getUserId();
      if (!userId) {
        console.warn("User ID not found. Cannot fetch attendance status.");
        setIsCheckInButtonDisabled(true); // Disable if user is not logged in
        return;
      }

      try {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`; //YYYY-MM-DD

        // Fetch attendance for the current user and date
        const response = await getRequest(`/config/attendance/?user_id=${userId}&attendance_date=${formattedDate}`);

        if (response && response.data && Array.isArray(response.data)) {
          // Check if any record for today has a check_in time
          const attendanceForTodayWithCheckIn = response.data.find(
            (record) => record.attendance_date === formattedDate && record.check_in !== null
          );

          if (attendanceForTodayWithCheckIn) {
            setIsCheckInButtonDisabled(true); // Disable if check_in data exists for today
            setAttendance(prevState => ({ ...prevState, checkIn: attendanceForTodayWithCheckIn.check_in })); // Store the raw ISO string
          } else {
            setIsCheckInButtonDisabled(false); // Enable if no check_in data for today
            setAttendance(prevState => ({ ...prevState, checkIn: '' })); // Clear checkIn if no record for today
          }
        } else {
          console.warn("Invalid API response for attendance:", response);
          setIsCheckInButtonDisabled(false); // Default to enabled if response is invalid
        }
      } catch (error) {
        console.error('Error fetching attendance status:', error.response?.data || error.message);
        setIsCheckInButtonDisabled(false); // Enable button in case of API error
      }
    };

    fetchAttendanceStatus();

    // Re-fetch attendance status at the start of each new day (approximate midnight refresh)
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0); // Set to next midnight
    const timeUntilMidnight = midnight.getTime() - now.getTime();

    const dailyRefreshTimer = setTimeout(fetchAttendanceStatus, timeUntilMidnight + 1000); // Fetch 1 second after midnight

    return () => clearTimeout(dailyRefreshTimer); // Cleanup the timer
  }, []); // Run once on mount and then daily

 // Fetch projects on component mount
useEffect(() => {
  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await getRequest('/config/project-config/');
      
      if (response && Array.isArray(response)) {
        const formattedProjects = response.map(project => ({
          id: project.id,
          name: project.name,
          icon: Ship,
          color: getColorForProject(project.id),
          ships: project.ships // Store ships array for each project
        }));
        
        setProjects([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800', ships: [] },
          ...formattedProjects
        ]);
      } else {
        console.error('Invalid projects response:', response);
        toast.error('Failed to load projects');
        setProjects([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800', ships: [] }
        ]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  fetchProjects();
}, []);

// Fetch ships when project is selected
useEffect(() => {
  const fetchShips = async () => {
    if (selectedProject && selectedProject !== 'all') {
      setLoadingShips(true);
      try {
        // Find the selected project to get its ships
        const selectedProjectData = projects.find(p => p.id === selectedProject);
        
        if (selectedProjectData && selectedProjectData.ships) {
          const formattedShips = selectedProjectData.ships.map(ship => ({
            id: ship.id,
            name: ship.name,
            icon: Anchor,
            color: getColorForShip(ship.id)
          }));
          
          setShips([
            { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' },
            ...formattedShips
          ]);
        } else {
          setShips([
            { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' }
          ]);
        }
      } catch (error) {
        console.error('Error processing ships:', error);
        toast.error('Failed to load ships');
      } finally {
        setLoadingShips(false);
      }
    } else {
      // Reset ships when "All" projects is selected
      setShips([
        { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' }
      ]);
      setSelectedShip('all');
    }
  };

  fetchShips();
}, [selectedProject, projects]); // Add projects to dependencies

// Fetch departments on component mount
useEffect(() => {
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await getRequest('/config/department-config/');
      
      // Check if response exists and has data array
      if (response && Array.isArray(response)) {
        const formattedDepartments = response.map(dept => ({
          id: dept.id,
          name: dept.name,
          icon: Folder,
          color: 'from-blue-500 to-blue-700'
        }));
        
        setDepartments([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' },
          ...formattedDepartments
        ]);
      } else {
        console.error('Invalid API response format:', response);
        toast.error('Received invalid department data from server');
        setDepartments([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
      setDepartments([
        { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' }
      ]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  fetchDepartments();
}, []);


  // Fetch task statistics when a ship or department is selected
  useEffect(() => {
    const fetchTaskStats = async () => {
      if (selectedShip && selectedShip !== 'all') {
        setLoadingTaskStats(true);
        try {
          let apiUrl = `/config/cards/?project_id=${selectedShip}`;

          // Add department_id if a specific department is selected
          if (selectedDepartment && selectedDepartment !== 'all') {
            apiUrl += `&department_id=${selectedDepartment}`;
          }

          const apiData = await getRequest(apiUrl);

          // Check if apiData exists, is an object, and is not an array
          if (apiData && typeof apiData === 'object' && !Array.isArray(apiData)) {
            setTaskStats({
              total_tasks: apiData.total_tasks || 0,
              in_progress_tasks: apiData.in_progress_tasks || 0,
              assigned_tasks: apiData.assigned_tasks || 0,
              not_assigned_tasks: apiData.not_assigned_tasks || 0,
              delayed_tasks: apiData.delayed_tasks || 0
            });
          } else {
            console.warn('API response data is invalid or missing:', apiData);
            setTaskStats({ // Reset to default zeros
              total_tasks: 0,
              in_progress_tasks: 0,
              assigned_tasks: 0,
              not_assigned_tasks: 0,
              delayed_tasks: 0
            });
            toast.error('Received invalid task statistics from the server.');
          }
        } catch (error) {
          console.error('Error fetching task statistics:', error.response?.data || error.message);
          toast.error('Failed to load task statistics.');
          // Reset stats on error
          setTaskStats({
            total_tasks: 0,
            in_progress_tasks: 0,
            assigned_tasks: 0,
            not_assigned_tasks: 0,
            delayed_tasks: 0
          });
        } finally {
          setLoadingTaskStats(false);
        }
      } else {
        // Reset stats when "All" ships is selected or no ship is selected
        setTaskStats({
          total_tasks: 0,
          in_progress_tasks: 0,
          assigned_tasks: 0,
          not_assigned_tasks: 0,
          delayed_tasks: 0
        });
      }
    };

    fetchTaskStats();
  }, [selectedShip, selectedDepartment]); // Depend on selectedShip and selectedDepartment

  const colorGradients = [
    'from-blue-500 to-blue-700',
    'from-green-500 to-green-700',
    'from-purple-500 to-purple-700',
    'from-indigo-500 to-indigo-700',
    'from-teal-500 to-teal-700',
    'from-emerald-500 to-emerald-700'
  ];

  const getColorForProject = (id) => {
    const index = projects.findIndex(p => p.id === id) % colorGradients.length;
    return colorGradients[index] || 'from-slate-500 to-slate-700';
  };

  const getColorForShip = (id) => {
    const index = ships.findIndex(s => s.id === id) % colorGradients.length;
    return colorGradients[index] || 'from-slate-500 to-slate-700';
  };

  const handleAttendance = async (type) => {
    const userId = getUserId();
    if (!userId) {
      toast.error("User ID not found. Please log in again.");
      console.error("User ID not found. Please log in again.");
      return;
    }

    const now = new Date();
    const attendanceDate = now.toISOString().split('T')[0]; //YYYY-MM-DD
    const isoTime = now.toISOString(); // ISO 8601 format

    let payload = {
      user: userId,
      attendance_date: attendanceDate,
      attendance_status: type === 'checkIn' ? 1 : 2, // 1 for check-in, 2 for check-out
    };

    try {
      if (type === 'checkIn') {
        payload.check_in = isoTime;
        payload.check_out = null; // Explicitly set to null for check-in
        const response = await postRequest('/config/attendance/', payload);
        console.log(`${type} response:`, response.data);
        setAttendance(prev => ({ ...prev, [type]: isoTime })); // Store the raw ISO string
        toast.success(`Check In successful!`);
        setIsCheckInButtonDisabled(true); // Disable after successful check-in
      } else if (type === 'checkOut') {
        // Step 1: Fetch the existing attendance record for today for the current user
        let existingRecordId = null;
        try {
          const response = await getRequest(`/config/attendance/?user_id=${userId}&attendance_date=${attendanceDate}`);
          if (response.data && response.data.length > 0) {
            // Assuming the API returns an array and we want the most recent or relevant one
            // Find an active check-in record (status 1 and check_out is null)
            const latestCheckIn = response.data.find(record => record.attendance_status === 1 && record.check_out === null);
            if (latestCheckIn) {
              existingRecordId = latestCheckIn.id;
              // Also update the checkIn state if it wasn't already set from the initial fetch
              setAttendance(prev => ({ ...prev, checkIn: latestCheckIn.check_in }));
            } else {
              toast.warn("No active check-in found for today. Please check in first.");
              return; // Stop the checkout process if no active check-in
            }
          } else {
            toast.warn("No attendance record found for today. Please check in first.");
            return; // Stop the checkout process if no record
          }
        } catch (fetchError) {
          console.error("Error fetching attendance record for checkout:", fetchError.response?.data || fetchError.message);
          toast.error("Failed to retrieve attendance data for checkout.");
          return; // Stop the checkout process on fetch error
        }

        if (existingRecordId) {
          payload.check_out = isoTime;
          // Step 2: Include the id in the payload for update
          payload.id = existingRecordId; // Add the ID to the payload
          const response = await postRequest('/config/attendance/', payload); // Assuming POST with ID acts as an update
          console.log(`${type} response:`, response.data);
          setAttendance(prev => ({ ...prev, [type]: isoTime })); // Store the raw ISO string
          toast.success(`Check Out successful!`);
          setIsCheckInButtonDisabled(false); // Enable after successful check-out (or re-enable for next check-in)
        }
      }
    } catch (error) {
      console.error(`Error during ${type}:`, error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || `Failed to ${type}.`;
      toast.error(errorMessage);
    }
  };

  const closeAllDropdowns = () => {
    setIsDepartmentDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsShipDropdownOpen(false);
  };

  const handleDropdownToggle = (dropdownType) => {
    switch(dropdownType) {
      case 'department':
        setIsDepartmentDropdownOpen(prev => !prev);
        setIsProjectDropdownOpen(false);
        setIsShipDropdownOpen(false);
        break;
      case 'project':
        setIsProjectDropdownOpen(prev => !prev);
        setIsDepartmentDropdownOpen(false);
        setIsShipDropdownOpen(false);
        break;
      case 'ship':
        setIsShipDropdownOpen(prev => !prev);
        setIsDepartmentDropdownOpen(false);
        setIsProjectDropdownOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-100 to-purple-100 relative overflow-hidden text-slate-800">
      {/* ToastContainer for notifications */}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-300/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-300/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Header */}
      <div className="relative z-10">
        <div className="flex items-center justify-between p-6 bg-white/80 backdrop-blur-md border-b border-blue-200 shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-xl shadow-lg">
              <Anchor size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">AWPS Dashboard</h1>
              <p className="text-blue-600 text-sm">Assistant Warship Production Superintendent Dashboard</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-slate-700 font-medium">{currentTime.toLocaleTimeString()}</p>
              <p className="text-blue-600 text-sm">{currentTime.toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Filter Dropdowns - Fixed z-index issue */}
          <div ref={dropdownRef} className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md relative z-10">
            <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
              <Filter className="mr-3 text-blue-500" size={24} />
              Filter Options
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Project Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Project</label>
                <button
                  onClick={() => handleDropdownToggle('project')}
                  className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-slate-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
                >
                  <span className="font-medium">
                    {loadingProjects
                      ? 'Loading...'
                      : (projects.find(p => p.id === selectedProject)?.name || 'Select Project')}
                  </span>
                  {isProjectDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {isProjectDropdownOpen && (
                  <div className="absolute top-full left-0 w-full bg-white border border-blue-200 rounded-lg shadow-xl mt-2 z-[200] max-h-60 overflow-y-auto">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          setSelectedProject(project.id);
                          closeAllDropdowns();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-2 hover:bg-blue-50 transition-colors ${
                          selectedProject === project.id ? 'bg-blue-100 font-semibold' : ''
                        }`}
                      >
                        <project.icon size={18} className={selectedProject === project.id ? 'text-blue-600' : 'text-slate-600'} />
                        <span>{project.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ship Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Ship</label>
                <button
                  onClick={() => handleDropdownToggle('ship')}
                  className={`w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-slate-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm ${
                    selectedProject === 'all' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={selectedProject === 'all' || loadingProjects || loadingShips}
                >
                  <span className="font-medium">
                    {loadingShips
                      ? 'Loading...'
                      : (ships.find(s => s.id === selectedShip)?.name || 'Select Ship')}
                  </span>
                  {isShipDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {isShipDropdownOpen && selectedProject !== 'all' && (
                  <div className="absolute top-full left-0 w-full bg-white border border-blue-200 rounded-lg shadow-xl mt-2 z-[200] max-h-60 overflow-y-auto">
                    {ships.map((ship) => (
                      <button
                        key={ship.id}
                        onClick={() => {
                          setSelectedShip(ship.id);
                          closeAllDropdowns();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-2 hover:bg-blue-50 transition-colors ${
                          selectedShip === ship.id ? 'bg-blue-100 font-semibold' : ''
                        }`}
                      >
                        <ship.icon size={18} className={selectedShip === ship.id ? 'text-blue-600' : 'text-slate-600'} />
                        <span>{ship.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
               {/* Department Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Department</label>
                <button
                  onClick={() => handleDropdownToggle('department')}
                  className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-slate-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
                >
                  <span className="font-medium">
                    {loadingDepartments
                      ? 'Loading...'
                      : (departments.find(d => d.id === selectedDepartment)?.name || 'Select Department')}
                  </span>
                  {isDepartmentDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {isDepartmentDropdownOpen && (
                  <div className="absolute top-full left-0 w-full bg-white border border-blue-200 rounded-lg shadow-xl mt-2 z-[200] max-h-60 overflow-y-auto">
                    {departments.map((dept) => (
                      <button
                        key={dept.id}
                        onClick={() => {
                          setSelectedDepartment(dept.id); // Set the ID, not the name
                          closeAllDropdowns();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-2 hover:bg-blue-50 transition-colors ${
                          selectedDepartment === dept.id ? 'bg-blue-100 font-semibold' : ''
                        }`}
                      >
                        <dept.icon size={18} className={selectedDepartment === dept.id ? 'text-blue-600' : 'text-slate-600'} />
                        <span>{dept.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid - Reduced z-index to prevent interference */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 relative z-0">
            {[
              { title: 'Total Tasks', value: loadingTaskStats ? 'Loading...' : taskStats.total_tasks, icon: Target, gradient: 'from-blue-400 to-indigo-500', change: '' },
              { title: 'In Progress', value: loadingTaskStats ? 'Loading...' : taskStats.in_progress_tasks, icon: Clock, gradient: 'from-orange-400 to-red-500', change: '' },
              { title: 'Assigned', value: loadingTaskStats ? 'Loading...' : taskStats.assigned_tasks, icon: Users, gradient: 'from-emerald-400 to-teal-500', change: '' },
              { title: 'Not Assigned', value: loadingTaskStats ? 'Loading...' : taskStats.not_assigned_tasks, icon: AlertCircle, gradient: 'from-yellow-400 to-orange-500', change: '' },
              // "Pending" is not in the API response, so setting N/A

              { title: 'Delayed', value: loadingTaskStats ? 'Loading...' : taskStats.delayed_tasks, icon: XCircle, gradient: 'from-red-400 to-rose-500', change: '' }
            ].map((stat, index) => (
              <div key={index} className="group relative">
                <div className="relative bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 hover:bg-white/90 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 bg-gradient-to-r ${stat.gradient} rounded-xl shadow-lg`}>
                      <stat.icon className="text-white" size={20} />
                    </div>
                    {stat.change && ( // Only render if change exists (can be removed if not needed)
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          stat.change.startsWith('+') ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {stat.change}
                        </span>
                    )}
                  </div>
                  <h3 className="text-slate-600 text-sm font-medium mb-1">{stat.title}</h3>
                  <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Attendance & Department Management */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
            {/* Attendance */}
            <div className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
                  <Clock className="mr-3 text-blue-500" size={24} />
                  Attendance Management
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleAttendance('checkIn')}
                    className={`group relative text-white p-4 rounded-xl font-semibold text-lg transition-all duration-300 transform shadow-lg flex items-center justify-center space-x-2
                      ${isCheckInButtonDisabled
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 hover:-translate-y-1'
                      }`}
                    disabled={isCheckInButtonDisabled} // Apply disabled state
                  >
                    <CheckCircle size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Check In</span>
                  </button>
                  <button
                    onClick={() => handleAttendance('checkOut')}
                    className="group relative bg-gradient-to-r from-red-500 to-rose-600 text-white p-4 rounded-xl font-semibold text-lg hover:from-red-600 hover:to-rose-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <XCircle size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Check Out</span>
                  </button>
                </div>
              </div>
              {(attendance.checkIn || attendance.checkOut) && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 text-slate-700 animate-fadeInUp">
                  {attendance.checkIn && (
                    <p className="text-emerald-600 text-sm mb-2 flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      <span className="font-medium">Checked in at:</span> {formatISTDateTime(attendance.checkIn)}
                    </p>
                  )}
                  {attendance.checkOut && (
                    <p className="text-red-600 text-sm flex items-center">
                      <XCircle size={16} className="mr-2" />
                      <span className="font-medium">Checked out at:</span> {formatISTDateTime(attendance.checkOut)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Department Management with Action Buttons */}
            <div className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
                  <CheckSquare className="mr-3 text-blue-500" size={24} />
                  Assign
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowDepartmentMapping(true)} // This will now open DepartmentWiseMapping
                    className="group relative bg-gradient-to-r from-blue-400 to-indigo-500 text-white p-4 rounded-xl font-semibold text-lg  transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Folder size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Department wise</span>
                  </button>

                </div>
              </div>
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
            {/* Employee Shift Calendar */}
            <div className={`bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col ${isEmployeeCalendarFullscreen ? 'fixed inset-0 z-[9999] bg-white/95 m-4' : ''}`} style={{ height: isEmployeeCalendarFullscreen ? 'auto' : '700px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <Calendar className="mr-3 text-blue-500" size={24} />
                  Employee Shift Calendar
                </h2>
                <button
                  onClick={() => setIsEmployeeCalendarFullscreen(!isEmployeeCalendarFullscreen)}
                  className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors text-blue-600"
                  title={isEmployeeCalendarFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
                >
                  {isEmployeeCalendarFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <EmployeeShiftCalender selectedProjectId={selectedProject} />
              </div>
            </div>

            {/* Work Order Calendar */}
            <div className={`bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col ${isWorkOrderCalendarFullscreen ? 'fixed inset-0 z-[9999] bg-white/95 m-4' : ''}`} style={{ height: isWorkOrderCalendarFullscreen ? 'auto' : '700px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <BarChart3 className="mr-3 text-blue-500" size={24} />
                  Work Order Calendar
                </h2>
                <button
                  onClick={() => setIsWorkOrderCalendarFullscreen(!isWorkOrderCalendarFullscreen)}
                  className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors text-blue-600"
                  title={isWorkOrderCalendarFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
                >
                  {isWorkOrderCalendarFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {/* Pass the selectedProject as a prop */}
                <WorkOrderCalender selectedProjectId={selectedProject} />
              </div>
            </div>

          </div>
            {/* Requisition Scheduler Calendar */}
            <div className={`bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col ${isRequisitionCalendarFullscreen ? 'fixed inset-0 z-[9999] bg-white/95 m-4' : ''}`} style={{ height: isRequisitionCalendarFullscreen ? 'auto' : '700px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <ClipboardList className="mr-3 text-blue-500" size={24} /> {/* Using ClipboardList for requisition */}
                  Requisition Calendar
                </h2>
                <button
                  onClick={() => setIsRequisitionCalendarFullscreen(!isRequisitionCalendarFullscreen)}
                  className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors text-blue-600"
                  title={isRequisitionCalendarFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
                >
                  {isRequisitionCalendarFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {/* Pass selectedShip as selectedProjectId and selectedDepartment */}
                <RequisitionScheduler selectedProjectId={selectedShip} selectedDepartment={selectedDepartment} />
              </div>
            </div>
        </div>
      </div>

      {/* New Pop-up Modal (Project Wise) */}
      {showNewPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg animate-fade-in p-6 relative">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">New Custom Pop-up</h3>
            <p className="text-slate-600 mb-6">This is a custom pop-up demonstrating a modal dialog with an overlay, similar to how `ShipDialog` is implemented in `ProjectMaster.jsx`.</p>
            <button
              onClick={() => setShowNewPopup(false)}
              className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors text-gray-600"
              title="Close"
            >
              <XCircle size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Department Wise Mapping Pop-up */}
      {showDepartmentMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-6xl h-5/6 overflow-y-auto">
            <DepartmentWiseMapping onBack={() => setShowDepartmentMapping(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistanceWarshipSuperintendentDashboard;